/**
 * In-app context help (D16, `manual/_maintenance/in-app-help.md`): each screen opens its page of the user manual.
 * The manual publishes `help-map.json` (route pattern → manual URL with an optional `#anchor`); the manual's base URL
 * is a runtime setting (`/runtime-config.json`, `manualUrl` / `manualUrlEn`), so a deployment changes it without a
 * new build. The English UI opens the English manual when one is configured, otherwise the Arabic manual.
 */

/** `manual/public/help-map.json`. */
export interface HelpMap {
  version: number;
  screens: Record<string, string>;
}

/** Runtime settings of the web app (`/runtime-config.json`). */
export interface RuntimeConfig {
  /** Base URL of the (Arabic) user manual. */
  manualUrl?: string;
  /** Base URL of the English user manual, when published. */
  manualUrlEn?: string;
}

/** Default manual location when nothing is configured: the manual served next to the app. */
export const defaultManualUrl = "/manual/";

/** Route prefix (first path segment) → manual module id of `module:<id>` keys. */
const moduleOfSegment: Record<string, string> = {
  gl: "general-ledger",
  md: "master-data",
  cash: "cash-and-banks",
  inv: "inventory",
  platform: "platform",
  quality: "quality",
  proc: "procurement",
  scm: "supply-chain",
  lease: "leasing",
  jewel: "jewellery",
  parking: "parking",
  fleet: "fleet",
};

/**
 * The manual path of a screen: the route pattern's own entry, else the concrete path (settings groups), else the
 * module chapter (`module:<id>`), else the manual home. A missing map never fails.
 */
export function resolveHelpPath(
  map: HelpMap | null | undefined,
  routeId: string | null | undefined,
  pathname: string,
): string {
  const screens = map?.screens ?? {};
  const concrete = pathname.replace(/\/+$/u, "");
  const found = [routeId ?? "", concrete === "" ? "/" : concrete].find(
    (key) => key !== "" && screens[key] !== undefined,
  );
  if (found !== undefined) return screens[found] ?? "/";
  const segment = pathname.split("/").find((part) => part !== "") ?? "";
  const module = moduleOfSegment[segment];
  const chapter = module ? screens[`module:${module}`] : undefined;
  return chapter ?? "/";
}

/** The manual base URL for the UI language (English falls back to the Arabic manual). */
export function manualBase(config: RuntimeConfig | null | undefined, language: string, fallback?: string): string {
  const arabic = filled(config?.manualUrl) ?? filled(fallback) ?? defaultManualUrl;
  const english = filled(config?.manualUrlEn);
  return language.startsWith("en") && english !== undefined ? english : arabic;
}

const filled = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === "" ? undefined : trimmed;
};

/** Joins the base URL and a manual path (keeps the `#anchor`, no double slashes). */
export function helpUrl(base: string, path: string): string {
  return `${base.replace(/\/+$/u, "")}/${path.replace(/^\/+/u, "")}`;
}
